import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.css";
import { FilterFunction, FilterContext } from "../filters/types";
import { i18n } from "../lib/i18n";
/**
 * Tom Select Stimulus Controller
 *
 * This controller initializes TomSelect on a select element with dynamic
 * client-side loading of options from an API endpoint. It supports
 * agnostic filtering through a filter registry system.
 *
 * Basic Usage:
 * <select data-controller="tom-select"
 *         data-tom-select-config-value='{"remoteUrl":"/api/v1/people/search"}'
 *         data-tom-select-options-value='{"placeholder":"Search for a person"}'>
 * </select>
 *
 * With Filtering:
 * <select data-controller="tom-select"
 *         data-tom-select-config-value='{
 *           "remoteUrl": "/api/v1/people/search",
 *           "filterCallback": "filterByRoomGender"
 *         }'
 *         data-tom-select-filter-context-value='{"roomGender": "male"}'>
 * </select>
 */
export default class extends Controller {
  static targets = ["select"];
  static values = {
    options: { type: Object, default: {} },
    config: { type: Object, default: {} },
    items: { type: Array, default: [] },
    optgroups: { type: Array, default: [] },
  };

  declare readonly selectTarget: HTMLSelectElement;
  declare readonly hasSelectTarget: boolean;
  declare readonly optionsValue: Record<string, any>;
  declare readonly hasOptionsValue: boolean;
  declare readonly configValue: Record<string, any>;
  declare readonly hasConfigValue: boolean;
  declare readonly itemsValue: any[];
  declare readonly hasItemsValue: boolean;
  declare readonly optgroupsValue: any[];
  declare readonly hasOptgroupsValue: boolean;
  declare tomSelect: TomSelect;
  // Focusing or typing asks for the dropdown; picking settles a single select.
  private dropdownWanted = false;

  connect() {
    this.initializeTomSelect();
  }

  initializeTomSelect() {
    const defaultConfig: any = {
      plugins: {
        clear_button: {
          title: i18n.t('tom_select.clear_selection'),
          className: "clear-button",
          html: (data: any) => {
            return `<div class="clear-button items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></div>`;
          },
        },
      },
      hidePlaceholder: true,
      searchField: this.searchFields,
      loadingClass: "",
      render: {
        option: this.renderOption.bind(this),
        item: this.renderSelected.bind(this),
        optgroup_header: this.renderOptgroupHeader.bind(this),
        loading: () => {
          return;
        },
        no_results: (data: any, escape: any) => {
          return `<div class="text-base text-center text-gray-800">${i18n.t('tom_select.no_results_found')}</div>`;
        },
      },
      onChange: this.handleChange.bind(this),
      onType: this.handleType.bind(this),
      onItemAdd: this.handleItemAdd.bind(this),
      onItemRemove: this.handleItemRemove.bind(this),
    };

    const selectElement = this.hasSelectTarget
      ? this.selectTarget
      : (this.element as HTMLSelectElement);

    // Turbo reconnects on DOM nodes that survived the visit. Adopt the live
    // instance rather than rebuilding: destroy() reverts the element to the
    // options it was built with, dropping anything the remote load added.
    const existing = (selectElement as any).tomselect;
    if (existing) {
      this.tomSelect = existing;
      return;
    }

    const originalSizeClass = this.tomSelectSizeClass(selectElement);
    selectElement.classList.remove("input", "input-bordered", "select", "select-bordered", "select-sm", "select-xs");

    // Merge all configuration options
    const configOptions: any = {
      ...defaultConfig,
      ...this.optionsValue,
    };

    // Remote loading only when there is somewhere to load from: with `load` set
    // but no URL, Tom Select waits for a callback that never comes and stays
    // "loading", which hides filtering and the create prompt for local options.
    if (this.configValue.remoteUrl) {
      configOptions.load = this.loadOptions.bind(this);
      configOptions.shouldLoad = () => true;
      configOptions.onFocus = this.handleFocus.bind(this);
    }

    // If multiple true
    if (this.optionsValue.multiple) {
      configOptions.maxItems = this.optionsValue.maxItems ?? 10;
      configOptions.plugins.remove_button = {
        title: i18n.t('tom_select.remove_item'),
      };
    }

    // Add optgroups if available
    if (this.hasOptgroupsValue && this.optgroupsValue.length > 0) {
      configOptions.optgroups = this.optgroupsValue;
    }

    // Add items if available
    if (this.hasItemsValue && this.itemsValue.length > 0) {
      configOptions.options = this.itemsValue;
    }

    this.tomSelect = new TomSelect(selectElement, configOptions);
    this.applySizeClass(originalSizeClass);
  }

  tomSelectSizeClass(selectElement: HTMLSelectElement) {
    if (selectElement.classList.contains("select-xs")) return "ts-select-xs";
    if (selectElement.classList.contains("select-sm")) return "ts-select-sm";

    return null;
  }

  applySizeClass(sizeClass: string | null) {
    if (!sizeClass || !this.tomSelect?.wrapper) return;

    this.tomSelect.wrapper.classList.add(sizeClass);
  }

  handleFocus() {
    this.dropdownWanted = true;

    // Load initial options when the field receives focus
    if (this.tomSelect) {
      this.tomSelect.load("");
    }
  }

  handleChange(value: any) {
    const detail = { value, tomSelect: this.tomSelect };
    const event = new CustomEvent("tom-select:change", {
      bubbles: true,
      detail,
    });
    this.element.dispatchEvent(event);
  }

  handleType() {
    this.dropdownWanted = true;
  }

  handleItemAdd(value: any, item: any) {
    this.settleSingleSelect();

    const detail = { value, item, tomSelect: this.tomSelect };
    const event = new CustomEvent("tom-select:item-added", {
      bubbles: true,
      detail,
    });
    this.element.dispatchEvent(event);
  }

  handleItemRemove(value: any, item: any) {
    const detail = { value, item, tomSelect: this.tomSelect };
    const event = new CustomEvent("tom-select:item-removed", {
      bubbles: true,
      detail,
    });
    this.element.dispatchEvent(event);
  }

  async loadOptions(query: any, callback: any) {
    if (!this.configValue.remoteUrl) return;

    const url = new URL(this.configValue.remoteUrl, window.location.origin);
    url.searchParams.append("q", query);

    // Add filter parameter if present (e.g., exclude_ids=1,2,3)
    if (this.configValue.filter) {
      url.search += (url.search ? "&" : "?") + this.configValue.filter;
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      let data = await response.json();
      const filteredData = await this.applyFilters(data, query);

      callback(filteredData);
      // The focus preload and the throttled search both outlive the choice they
      // were meant for. Tom Select reopens the dropdown for every answer, and
      // its open() queues a focus pass that reopens it once more, so a dropdown
      // nobody asked for is closed after that pass rather than during it.
      if (!this.dropdownWanted && this.settledSingle()) setTimeout(() => this.tomSelect.close());
    } catch (error) {
      console.error(error);
      callback();
    }
  }

  private settledSingle(): boolean {
    return this.tomSelect?.settings?.maxItems === 1 && this.tomSelect.items.length > 0;
  }

  // Picking closes the dropdown, but the search it ends leaves timers behind
  // that reopen it a moment later, over whatever sits under the field: the
  // throttled refresh of the query the user typed, and the focus pass that
  // Tom Select's open() queues. Drop the refresh, and openOnFocus for the tick
  // that pass lands in.
  private settleSingleSelect() {
    if (!this.settledSingle()) return;

    this.dropdownWanted = false;
    window.clearTimeout((this.tomSelect as any).refreshTimeout);
    const openOnFocus = this.tomSelect.settings.openOnFocus;
    this.tomSelect.settings.openOnFocus = false;
    setTimeout(() => { this.tomSelect.settings.openOnFocus = openOnFocus; });
  }

  async applyFilters(data: any[], query: string): Promise<any[]> {
    if (this.configValue.filter?.callback) {
      const filterFunction = await import(
        `../filters/${this.configValue.filter.callback}.ts`
      ).then((module) => module.default);

      if (filterFunction && typeof filterFunction === "function") {
        const context: FilterContext = {
          query,
          ...this.configValue.filter?.values, // Spread all filter context values
        };

        return filterFunction(data, context);
      } else {
        console.warn(
          `Filter function '${this.configValue.filter?.callback}' not found`,
        );
      }
    }

    return data;
  }

  renderOption(data: any, escape: any) {
    const optionClasses = this.configValue.optionClasses || "";

    // Check for option_html in various places
    if (
      data.$option &&
      data.$option.dataset &&
      data.$option.dataset.optionHtml
    ) {
      return data.$option.dataset.optionHtml;
    } else if (data.option_html) {
      return data.option_html;
    } else {
      return `<div class="${optionClasses} text-sm">
                ${escape(data[this.optionsValue.labelField || "text"])}
              </div>`;
    }
  }

  renderSelected(data: any, escape: any) {
    const selectedClasses = this.configValue.selectedClasses || "";
    const dataset = data.$option?.dataset;

    // data-item-html lets a rich dropdown row collapse to something compact
    // once chosen; otherwise the option markup is reused for both.
    if (dataset?.itemHtml) {
      return dataset.itemHtml;
    } else if (dataset?.optionHtml) {
      return dataset.optionHtml;
    } else if (data.option_html) {
      return data.option_html;
    } else {
      return `<div class="${selectedClasses}">
                ${escape(data[this.optionsValue.labelField || "text"])}
              </div>`;
    }
  }

  renderOptgroupHeader(data: any, escape: any) {
    return `<div class="optgroup-header text-xs capitalize text-gray-500 border-t border-gray-400 pt-2 font-medium p-2 mt-2">
      ${escape(data.label)}
    </div>`;
  }

  get searchFields() {
    return ["text", this.optionsValue.labelField].filter(Boolean);
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
    }
  }
}
